import { useEffect, useState } from "react";
import { toast } from "sonner";
import platformApi from "@/lib/platformApi";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PauseCircle, PlayCircle, Clock, BadgeCheck } from "lucide-react";

const STAT_LABELS = [
  ["employees", "Employees"], ["users", "Users"], ["documents", "Documents"],
  ["payroll_runs", "Payroll runs"], ["gateway_audits", "Gateway audits"],
  ["notifications", "Notifications"],
];

const billingLabel = (t) => {
  const b = t.billing || {};
  if (b.subscription_id && b.status === "active") return "paid (PayPal)";
  if (b.cancel_at_period_end) return "cancelled — active until period end";
  if (b.comped) return t.subdomain === "vertex" ? "comped · internal" : "comped";
  if (t.status === "trial") return "trial — not billed";
  if (t.status === "past_due") return "past due — grace period";
  return "not billed";
};

export const TenantDetailPanel = ({ subdomain, onChanged }) => {
  const [tenant, setTenant] = useState(null);

  const load = () => platformApi.get(`/tenants/${subdomain}`).then((r) => setTenant(r.data));
  useEffect(() => { setTenant(null); load(); }, [subdomain]); // eslint-disable-line

  const transition = async (action) => {
    try {
      await platformApi.post(`/tenants/${subdomain}/${action}`);
      toast.success(`Tenant ${subdomain} ${action === "suspend" ? "suspended" : "reactivated"}`);
      load(); onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const extendTrial = async () => {
    try {
      const { data } = await platformApi.post(`/tenants/${subdomain}/extend-trial`, { days: 14 });
      toast.success(`Trial extended — now ends ${String(data.trial_ends_at).slice(0, 10)}`);
      load(); onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const convertComped = async () => {
    try {
      await platformApi.post(`/tenants/${subdomain}/convert-to-active`);
      toast.success(`${subdomain} converted to active — marked as COMPED (not paid revenue)`);
      load(); onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!tenant) return <Skeleton className="h-64 w-full rounded-none" />;
  const suspended = tenant.status === "suspended";

  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="tenant-detail-panel">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-[15px] font-bold text-[#1E293B]">{tenant.org_name}</h3>
          <p className="text-[11px] font-mono text-slate-400 mt-0.5">
            {tenant.subdomain}.aetriaos.com · {tenant.plan} · since {String(tenant.created_at).slice(0, 10)}
          </p>
        </div>
        <span data-testid="tenant-detail-status"
          className={`text-[9px] font-mono uppercase px-2 py-1 border ${
            suspended ? "bg-red-50 border-red-200 text-red-700"
                      : "bg-teal-50 border-teal-200 text-teal-700"}`}>
          {tenant.status}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        {STAT_LABELS.map(([k, label]) => (
          <div key={k} className="border border-slate-100 px-3 py-2" data-testid={`tenant-usage-${k}`}>
            <p className="text-[9px] uppercase tracking-[0.12em] text-slate-400">{label}</p>
            <p className="text-[15px] font-bold font-mono text-[#1E293B]">
              {tenant.usage[k]}
              {k === "employees" && tenant.employee_cap ? (
                <span className="text-[10px] text-slate-400 font-normal"> / {tenant.employee_cap}</span>
              ) : null}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex justify-end">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            {suspended ? (
              <Button data-testid="reactivate-tenant-btn"
                className="rounded-none h-8 text-[11px] brand-accent-bg hover:bg-teal-700 text-white">
                <PlayCircle className="h-3.5 w-3.5" /> Reactivate
              </Button>
            ) : (
              <Button data-testid="suspend-tenant-btn" variant="outline"
                className="rounded-none h-8 text-[11px] border-red-300 text-red-700 hover:bg-red-50">
                <PauseCircle className="h-3.5 w-3.5" /> Suspend
              </Button>
            )}
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-none">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {suspended ? `Reactivate ${tenant.org_name}?` : `Suspend ${tenant.org_name}?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {suspended
                  ? "Users of this tenant will immediately regain login and API access."
                  : "All users of this tenant will be blocked from login and API access and shown an \"Account suspended — contact your provider\" screen."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="tenant-transition-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="tenant-transition-confirm"
                className={`rounded-none ${suspended ? "brand-accent-bg hover:bg-teal-700" : "bg-red-600 hover:bg-red-700"}`}
                onClick={() => transition(suspended ? "reactivate" : "suspend")}>
                {suspended ? "Reactivate" : "Suspend tenant"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default TenantDetailPanel;
